import { fg } from '@atlaskit/platform-feature-flags/fg';

import { NounKeys } from '../../../../common/constants/nouns';
import { PrimaryProductKeys } from '../../../../common/constants/primary-product-keys';
import { ProductKeys, type PrimaryProductKey } from '../../../../common/constants/product-keys';
import { type AppOption } from '../../../../common/utils/app-filter-options';

/**
 * Ranking for connected apps in the Lite-mode app filter pill bar.
 *
 * See: https://atlassian.slack.com/archives/C0AM7ADEAH3/p1780021272236159
 */
export const LITE_CONNECTED_APP_RANKING: ReadonlyArray<ProductKeys | NounKeys> = [
	ProductKeys.Jira,
	ProductKeys.Confluence,
	ProductKeys.GoogleDrive,
	ProductKeys.Loom,
	ProductKeys.Sharepoint,
	NounKeys.project,
	NounKeys.goal,
	ProductKeys.Slack,
	ProductKeys.Figma,
	ProductKeys.Github,
	ProductKeys.Miro,
	ProductKeys.Salesforce,
	ProductKeys.Bitbucket,
];

/**
 * Confluence-specific Lite connected app ranking.
 *
 * Static popularity-based order. The first 15 entries are the popularity-ranked
 * apps; `project`, `goal`, and Salesforce are intentionally kept at the end (they
 * are not part of the static popularity order but are retained so they keep a
 * defined position ahead of any unranked app).
 */
export const CONFLUENCE_LITE_CONNECTED_APP_RANKING: ReadonlyArray<ProductKeys | NounKeys> = [
	ProductKeys.Confluence,
	ProductKeys.Jira,
	ProductKeys.GoogleDrive,
	ProductKeys.Sharepoint,
	ProductKeys.Slack,
	ProductKeys.Github,
	ProductKeys.JiraServiceManagement,
	ProductKeys.Figma,
	ProductKeys.Bitbucket,
	ProductKeys.Assets,
	ProductKeys.Loom,
	ProductKeys.Miro,
	ProductKeys.Teams,
	ProductKeys.OneDrive,
	NounKeys.project,
	NounKeys.goal,
	ProductKeys.Salesforce,
];

/**
 * Loom-specific Lite connected app ranking.
 *
 * Keep the default relative order, but surface Loom immediately after the "All" pill
 * when Search Page is hosted by Loom.
 */
export const LOOM_LITE_CONNECTED_APP_RANKING: ReadonlyArray<ProductKeys | NounKeys> = [
	ProductKeys.Loom,
	...LITE_CONNECTED_APP_RANKING.filter((product) => product !== ProductKeys.Loom),
];

const REFRESH_CONNECTOR_ORDER: ReadonlyArray<ProductKeys | NounKeys> = [
	ProductKeys.GoogleDrive,
	ProductKeys.Confluence,
	ProductKeys.Jira,
	ProductKeys.Slack,
	ProductKeys.Loom,
	ProductKeys.Zoom,
	ProductKeys.Bitbucket,
	ProductKeys.Gong,
	ProductKeys.JiraServiceManagement,
	ProductKeys.Salesforce,
	NounKeys.goal,
	ProductKeys.Miro,
	ProductKeys.Compass,
	ProductKeys.Focus,
];

const REFRESH_TOP_PRODUCT_BY_HOST: Partial<Record<PrimaryProductKey, ProductKeys>> = {
	[PrimaryProductKeys.Jira]: ProductKeys.Jira,
	[PrimaryProductKeys.Confluence]: ProductKeys.Confluence,
	[PrimaryProductKeys.Loom]: ProductKeys.Loom,
};

/**
 * Connected app ranking for the refreshed FPS filter row.
 *
 * The host product leads when it is Jira, Confluence or Loom, otherwise Confluence does. The
 * host's existing ranking is appended so apps outside the new order keep their relative order.
 */
export const getRefreshConnectorOrderRanking = (
	primaryProduct: PrimaryProductKey | undefined,
): ReadonlyArray<ProductKeys | NounKeys> => {
	const top =
		(primaryProduct && REFRESH_TOP_PRODUCT_BY_HOST[primaryProduct]) ?? ProductKeys.Confluence;

	return [
		...new Set([top, ...REFRESH_CONNECTOR_ORDER, ...getLiteConnectedAppRanking(primaryProduct)]),
	];
};

/**
 * Returns the effective Lite connected app ranking based on the host primary product.
 *
 * When the host is Confluence, the Confluence-specific popularity-based order
 * ({@link CONFLUENCE_LITE_CONNECTED_APP_RANKING}) is used. For every other host the
 * default {@link LITE_CONNECTED_APP_RANKING} is returned unchanged.
 *
 * `isRefreshedFilterRow` callers get {@link getRefreshConnectorOrderRanking} instead when the
 * connector-order gate is on; the Lite pill bar never passes it, so its order is unaffected.
 *
 * Note: this function is only reached when the host has opted into the Lite experience.
 */
export const getLiteConnectedAppRanking = (
	primaryProduct: PrimaryProductKey | undefined,
	{ isRefreshedFilterRow = false }: { isRefreshedFilterRow?: boolean } = {},
): ReadonlyArray<ProductKeys | NounKeys> => {
	if (isRefreshedFilterRow && fg('rovo-search-team-eu-26-fps-refresh-connector-order')) {
		return getRefreshConnectorOrderRanking(primaryProduct);
	}

	if (primaryProduct === PrimaryProductKeys.Confluence) {
		return CONFLUENCE_LITE_CONNECTED_APP_RANKING;
	}

	if (primaryProduct === PrimaryProductKeys.Loom) {
		return LOOM_LITE_CONNECTED_APP_RANKING;
	}

	return LITE_CONNECTED_APP_RANKING;
};

export const rankConnectedAppsForLite = (
	authenticatedOptions: AppOption[],
	ranking: ReadonlyArray<ProductKeys | NounKeys> = LITE_CONNECTED_APP_RANKING,
): AppOption[] => {
	const rankIndex = new Map<string, number>(ranking.map((key, index) => [String(key), index]));

	const decorated = authenticatedOptions.map((option, originalIndex) => {
		const rank = rankIndex.get(String(option.value));
		return {
			option,
			rank: rank === undefined ? Number.POSITIVE_INFINITY : rank,
			originalIndex,
		};
	});

	decorated.sort((a, b) => {
		if (a.rank !== b.rank) {
			return a.rank - b.rank;
		}
		return a.originalIndex - b.originalIndex;
	});

	return decorated.map((d) => d.option);
};
